import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel,
  Grid,
  Tooltip,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Delete, Add, Download, Save, CallSplit } from "@mui/icons-material";
import { apiUrl } from "../apiConfig.js";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const METODOS_PAGO = ["Efectivo", "TPV", "Transferencia", "Otro"];

const IVA_RATE = 0.16;
const IEPS_RATE = 0.08;

const uid = () =>
  (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const formatCurrency = (value) =>
  (Number(value) || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Deriva IVA / IEPS / total (con impuesto) hacia adelante desde el subtotal
// (la venta real). Refleja exactamente la fórmula del servidor.
const derivar = (subtotal, descuento, devolucion, llevaIva, llevaIeps) => {
  const st = toNumber(subtotal);
  const desc = toNumber(descuento);
  const dev = toNumber(devolucion);
  const base = st - desc;
  const iva = llevaIva ? round2(base * IVA_RATE) : 0;
  const ieps = llevaIeps ? round2(base * IEPS_RATE) : 0;
  const total = round2(st - desc + iva + ieps - dev);
  return { iva, ieps, total };
};

const diasCicloDe = (fechaVenta, fechaCobro) => {
  if (!fechaVenta || !fechaCobro) return null;
  const dias = Math.round(
    (new Date(fechaCobro) - new Date(fechaVenta)) / 86400000
  );
  return Math.max(0, dias);
};

function Ventas() {
  const navigate = useNavigate();
  const now = new Date();

  const [selectedPeriod, setSelectedPeriod] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });

  const [ventas, setVentas] = useState([]);
  const [productos, setProductos] = useState([]); // conceptos con agrupacion === "Venta"

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const [resumen, setResumen] = useState(null);
  const [desgloseVentaId, setDesgloseVentaId] = useState(null);

  const primerDiaDelMes = useCallback(() => {
    const { year, month } = selectedPeriod;
    return `${year}-${String(month).padStart(2, "0")}-01`;
  }, [selectedPeriod]);

  // ------------------------------------------------------------------
  // Carga del periodo (catálogo de productos + ventas guardadas)
  // ------------------------------------------------------------------
  const loadPeriod = useCallback(async (year, month) => {
    setLoading(true);
    setError("");
    setStatus("");
    const token = sessionStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [catalogoRes, ventasRes] = await Promise.all([
        fetch(apiUrl("api/v1/catalogo/conceptos"), { headers }),
        fetch(apiUrl(`api/v1/ventas?year=${year}&month=${month}`), { headers }),
      ]);

      if ([catalogoRes, ventasRes].some((r) => r.status === 401 || r.status === 403)) {
        sessionStorage.removeItem("token");
        navigate("/login");
        return;
      }

      const catalogo = catalogoRes.ok ? await catalogoRes.json() : { conceptos: [] };
      const ventasGuardadas = ventasRes.ok ? await ventasRes.json() : [];

      setProductos((catalogo.conceptos || []).filter((c) => c.agrupacion === "Venta"));
      setVentas((ventasGuardadas || []).map((v) => ({ ...v, id: uid() })));
    } catch (err) {
      console.error("Error cargando ventas:", err);
      setError("Error al cargar los datos. Intente recargar la página.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadPeriod(selectedPeriod.year, selectedPeriod.month);
  }, [selectedPeriod.year, selectedPeriod.month, loadPeriod]);

  const nuevaVenta = () => ({
    id: uid(),
    fechaVenta: primerDiaDelMes(),
    fechaCobro: "",
    folio: "",
    metodoPago: "Efectivo",
    facturada: true,
    llevaIva: true,
    llevaIeps: false,
    subtotal: "",
    descuento: 0,
    devolucion: 0,
    desglose: [],
  });

  // ------------------------------------------------------------------
  // Edición de filas
  // ------------------------------------------------------------------
  const updateVenta = (id, field, value) => {
    setVentas((prev) => prev.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
  };

  const deleteVenta = (id) => setVentas((prev) => prev.filter((v) => v.id !== id));
  const addVenta = () => setVentas((prev) => [...prev, nuevaVenta()]);

  // ------------------------------------------------------------------
  // Guardar / Excel
  // ------------------------------------------------------------------
  const persistVentas = useCallback(async (filas, year, month) => {
    const token = sessionStorage.getItem("token");
    const payload = filas.map(({ id, iva, ieps, total, diasCiclo, ...rest }) => ({
      ...rest,
      fechaCobro: rest.fechaCobro || null,
      subtotal: toNumber(rest.subtotal),
      descuento: toNumber(rest.descuento),
      devolucion: toNumber(rest.devolucion),
      desglose: (rest.desglose || []).map((d) => ({
        conceptoCatalogo: d.conceptoCatalogo || "",
        unidadNegocio: d.unidadNegocio || "",
        importe: toNumber(d.importe),
      })),
    }));

    const res = await fetch(
      apiUrl(`api/v1/ventas/update?year=${year}&month=${month}`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (res.status === 401 || res.status === 403) {
      sessionStorage.removeItem("token");
      navigate("/login");
      return null;
    }
    if (!res.ok) throw new Error("No se pudieron guardar las ventas.");

    const guardadas = await res.json();
    return guardadas.map((v) => ({ ...v, id: uid(), fechaCobro: v.fechaCobro || "" }));
  }, [navigate]);

  const loadResumen = useCallback(async (year, month) => {
    const token = sessionStorage.getItem("token");
    try {
      const res = await fetch(
        apiUrl(`api/v1/ventas/resumen?year=${year}&month=${month}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setResumen(await res.json());
    } catch (err) {
      console.error("Error cargando resumen:", err);
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setStatus("");
    try {
      const { year, month } = selectedPeriod;
      const guardadas = await persistVentas(ventas, year, month);
      if (guardadas) {
        setVentas(guardadas);
        setStatus("Guardado con éxito");
        await loadResumen(year, month);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!loading) loadResumen(selectedPeriod.year, selectedPeriod.month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleExportExcel = async () => {
    try {
      setLoadingExcel(true);
      const token = sessionStorage.getItem("token");
      const { year, month } = selectedPeriod;
      const res = await fetch(
        apiUrl(`api/v1/ventas/resumen/excel?year=${year}&month=${month}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Error al generar el Excel");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ventas-${year}-${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel error:", err);
      setError("No se pudo descargar el archivo Excel.");
    } finally {
      setLoadingExcel(false);
    }
  };

  const handlePeriodChange = (field, value) =>
    setSelectedPeriod((prev) => ({ ...prev, [field]: value }));

  // ------------------------------------------------------------------
  // Totales en vivo (cliente)
  // ------------------------------------------------------------------
  const totalesLive = useMemo(() => {
    let subtotal = 0, impuestos = 0, total = 0;
    ventas.forEach((v) => {
      const d = derivar(v.subtotal, v.descuento, v.devolucion, v.llevaIva, v.llevaIeps);
      subtotal += toNumber(v.subtotal);
      impuestos += d.iva + d.ieps;
      total += d.total;
    });
    return { subtotal: round2(subtotal), impuestos: round2(impuestos), total: round2(total) };
  }, [ventas]);

  const desgloseVenta = ventas.find((v) => v.id === desgloseVentaId) || null;

  const uniformSx = {
    "& .MuiOutlinedInput-root": { height: 40 },
    "& .MuiOutlinedInput-input, & .MuiSelect-select": {
      padding: "8px 12px",
      lineHeight: "24px",
      fontSize: "14px",
    },
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={60} sx={{ mt: 10 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Cabecera */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h4" fontWeight="bold">Ventas</Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Año</InputLabel>
                  <Select
                    value={selectedPeriod.year}
                    label="Año"
                    onChange={(e) => handlePeriodChange("year", e.target.value)}
                  >
                    {Array.from(
                      { length: now.getFullYear() - 2022 },
                      (_, i) => 2023 + i
                    ).map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button variant="outlined" onClick={handleExportExcel} disabled={loadingExcel || saving} startIcon={<Download />}>
                  {loadingExcel ? "Generando..." : "Generar Excel"}
                </Button>
                <Button variant="contained" color="success" onClick={() => navigate("/")} disabled={saving}>
                  Regresar
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={<Save />}>
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {MONTHS.map((m, i) => {
                const isFuture =
                  selectedPeriod.year === now.getFullYear() && i + 1 > now.getMonth() + 1;
                return (
                  <Chip
                    key={m}
                    label={m.substring(0, 3)}
                    size="small"
                    disabled={isFuture}
                    variant={selectedPeriod.month === i + 1 ? "filled" : "outlined"}
                    color={selectedPeriod.month === i + 1 ? "primary" : "default"}
                    onClick={() => !isFuture && handlePeriodChange("month", i + 1)}
                    sx={{ fontWeight: selectedPeriod.month === i + 1 ? 700 : 400 }}
                  />
                );
              })}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {status && (
        <Alert severity={status.includes("éxito") ? "success" : "info"} sx={{ mb: 3 }}>{status}</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Tabla de ventas */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Ventas del mes"
          subheader="Captura la venta (subtotal sin IVA). El IVA y el total se calculan. La fecha de cobro mide el ciclo financiero."
          action={
            <Button variant="contained" size="small" startIcon={<Add />} onClick={addVenta}>
              Agregar venta
            </Button>
          }
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Fecha venta</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Folio</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Método de pago</TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>¿IVA?</TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>Subtotal</TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>IVA</TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Fecha cobro</TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Ciclo</TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Desglose</TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Borrar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ventas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                        No hay ventas en este mes. Usa "Agregar venta" para capturar tu facturación.
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {ventas.map((v) => {
                  const d = derivar(v.subtotal, v.descuento, v.devolucion, v.llevaIva, v.llevaIeps);
                  const ciclo = diasCicloDe(v.fechaVenta, v.fechaCobro);
                  const sumaDesglose = round2(
                    (v.desglose || []).reduce((s, l) => s + toNumber(l.importe), 0)
                  );
                  const desgloseOk =
                    (v.desglose || []).length > 0 &&
                    Math.abs(sumaDesglose - toNumber(v.subtotal)) < 0.01;
                  return (
                    <TableRow key={v.id} hover>
                      <TableCell sx={{ minWidth: 150 }}>
                        <TextField
                          type="date"
                          value={v.fechaVenta || ""}
                          onChange={(e) => updateVenta(v.id, "fechaVenta", e.target.value)}
                          size="small" fullWidth sx={uniformSx}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <TextField
                          value={v.folio || ""}
                          onChange={(e) => updateVenta(v.id, "folio", e.target.value)}
                          placeholder="Folio" size="small" fullWidth sx={uniformSx}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={v.metodoPago || "Efectivo"}
                            onChange={(e) => updateVenta(v.id, "metodoPago", e.target.value)}
                            sx={uniformSx}
                          >
                            {METODOS_PAGO.map((m) => (
                              <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Checkbox
                          checked={!!v.llevaIva}
                          onChange={(e) => updateVenta(v.id, "llevaIva", e.target.checked)}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <TextField
                          value={v.subtotal}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9.]/g, "");
                            if ((raw.match(/\./g) || []).length > 1) return;
                            updateVenta(v.id, "subtotal", raw);
                          }}
                          onBlur={(e) => updateVenta(v.id, "subtotal", toNumber(e.target.value))}
                          placeholder="$0.00" size="small" fullWidth sx={uniformSx}
                          inputProps={{ style: { textAlign: "right" } }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        ${formatCurrency(d.iva + d.ieps)}
                      </TableCell>
                      <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                        ${formatCurrency(d.total)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <TextField
                          type="date"
                          value={v.fechaCobro || ""}
                          onChange={(e) => updateVenta(v.id, "fechaCobro", e.target.value)}
                          size="small" fullWidth sx={uniformSx}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        {ciclo == null ? (
                          <Chip size="small" variant="outlined" color="warning" label="Por cobrar" />
                        ) : (
                          <Chip size="small" variant="outlined" color={ciclo === 0 ? "success" : "default"}
                            label={ciclo === 0 ? "Contado" : `${ciclo} d`} />
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        <Tooltip title={desgloseOk ? "Desglose completo" : "Desglose incompleto"}>
                          <span>
                            <Button
                              size="small"
                              variant={desgloseOk ? "outlined" : "text"}
                              color={desgloseOk ? "success" : "warning"}
                              startIcon={<CallSplit />}
                              onClick={() => setDesgloseVentaId(v.id)}
                            >
                              {(v.desglose || []).length > 0 ? `${v.desglose.length}` : "Desglosar"}
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <IconButton color="error" size="small" onClick={() => deleteVenta(v.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Totales rápidos */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatCard titulo="Subtotal (sin IVA)" valor={`$${formatCurrency(totalesLive.subtotal)}`} />
        <StatCard titulo="Impuestos (IVA/IEPS)" valor={`$${formatCurrency(totalesLive.impuestos)}`} />
        <StatCard titulo="Total (con IVA)" valor={`$${formatCurrency(totalesLive.total)}`} />
        {resumen?.totales && (
          <StatCard
            titulo="Ciclo promedio de cobro"
            valor={`${toNumber(resumen.totales.diasPromedioCobro).toFixed(1)} días`}
            sub={`Cobrado $${formatCurrency(resumen.totales.subtotalCobrado)} · Por cobrar $${formatCurrency(resumen.totales.subtotalPorCobrar)}`}
          />
        )}
      </Grid>

      {/* Análisis (resumen del servidor) */}
      {resumen && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <AnalisisTabla titulo="Venta por producto" filas={resumen.porProducto} />
          </Grid>
          <Grid item xs={12} md={4}>
            <AnalisisTabla titulo="Venta por unidad de negocio" filas={resumen.porUnidadNegocio} />
          </Grid>
          <Grid item xs={12} md={4}>
            <AnalisisTabla titulo="Venta por método de pago" filas={resumen.porMetodoPago} />
          </Grid>
        </Grid>
      )}

      <DesgloseDialog
        venta={desgloseVenta}
        productos={productos}
        onClose={() => setDesgloseVentaId(null)}
        onSave={(updated) => {
          setVentas((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
          setDesgloseVentaId(null);
        }}
      />
    </Container>
  );
}

function StatCard({ titulo, valor, sub }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">{titulo}</Typography>
          <Typography variant="h5" fontWeight="bold">{valor}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </CardContent>
      </Card>
    </Grid>
  );
}

// ====================================================================
// Tabla de análisis agrupado
// ====================================================================
function AnalisisTabla({ titulo, filas }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardHeader title={titulo} titleTypographyProps={{ variant: "subtitle1", fontWeight: "bold" }} />
      <Divider />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Concepto</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>Subtotal</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!filas || filas.length === 0) && (
              <TableRow>
                <TableCell colSpan={3} sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
                  Sin datos. Guarda las ventas desglosadas.
                </TableCell>
              </TableRow>
            )}
            {(filas || []).map((f) => (
              <TableRow key={f.clave}>
                <TableCell>{f.clave}</TableCell>
                <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>${formatCurrency(f.subtotal)}</TableCell>
                <TableCell sx={{ textAlign: "right" }}>{(toNumber(f.porcentaje) * 100).toFixed(1)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

// ====================================================================
// Dialog de desglose de una venta
// ====================================================================
function DesgloseDialog({ venta, productos, onClose, onSave }) {
  const [lineas, setLineas] = useState([]);
  const [facturada, setFacturada] = useState(true);
  const [llevaIeps, setLlevaIeps] = useState(false);
  const [descuento, setDescuento] = useState(0);
  const [devolucion, setDevolucion] = useState(0);

  useEffect(() => {
    if (venta) {
      setLineas((venta.desglose || []).map((d) => ({ ...d, id: uid() })));
      setFacturada(venta.facturada !== false);
      setLlevaIeps(!!venta.llevaIeps);
      setDescuento(venta.descuento || 0);
      setDevolucion(venta.devolucion || 0);
    }
  }, [venta]);

  if (!venta) return null;

  const objetivo = round2(toNumber(venta.subtotal));
  const suma = round2(lineas.reduce((s, l) => s + toNumber(l.importe), 0));
  const diferencia = round2(objetivo - suma);
  const sumaOk = Math.abs(diferencia) < 0.01 && lineas.length > 0;

  const addLinea = () =>
    setLineas((prev) => [...prev, { id: uid(), conceptoCatalogo: "", unidadNegocio: "", importe: "" }]);

  const updateLinea = (id, field, value) => {
    setLineas((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        if (field === "conceptoCatalogo") {
          const prod = productos.find((p) => p.conceptoCatalogo === value);
          updated.unidadNegocio = prod?.unidadNegocio || "";
        }
        return updated;
      })
    );
  };

  const removeLinea = (id) => setLineas((prev) => prev.filter((l) => l.id !== id));

  const distribuirRestante = () => {
    if (lineas.length === 0) return;
    setLineas((prev) => {
      const copia = [...prev];
      const last = copia[copia.length - 1];
      copia[copia.length - 1] = { ...last, importe: round2(toNumber(last.importe) + diferencia) };
      return copia;
    });
  };

  const handleGuardar = () => {
    onSave({
      ...venta,
      facturada,
      llevaIeps,
      descuento: toNumber(descuento),
      devolucion: toNumber(devolucion),
      desglose: lineas.map(({ id, ...rest }) => ({
        conceptoCatalogo: rest.conceptoCatalogo || "",
        unidadNegocio: rest.unidadNegocio || "",
        importe: toNumber(rest.importe),
      })),
    });
  };

  return (
    <Dialog open={!!venta} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Desglose de venta {venta.folio ? `— Folio ${venta.folio}` : ""}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Subtotal a desglosar</Typography>
            <Typography fontWeight="bold">${formatCurrency(objetivo)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel
              control={<Checkbox checked={facturada} onChange={(e) => setFacturada(e.target.checked)} />}
              label="Facturada"
            />
            <FormControlLabel
              control={<Checkbox checked={llevaIeps} onChange={(e) => setLlevaIeps(e.target.checked)} />}
              label="IEPS"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Descuento" size="small" value={descuento}
              onChange={(e) => setDescuento(e.target.value.replace(/[^0-9.]/g, ""))}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Devolución" size="small" value={devolucion}
              onChange={(e) => setDevolucion(e.target.value.replace(/[^0-9.]/g, ""))}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Producto / concepto</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Unidad de negocio</TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>Importe (sin IVA)</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.map((l) => (
              <TableRow key={l.id}>
                <TableCell sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={l.conceptoCatalogo || ""}
                      displayEmpty
                      onChange={(e) => updateLinea(l.id, "conceptoCatalogo", e.target.value)}
                    >
                      <MenuItem value=""><em>Selecciona producto</em></MenuItem>
                      {productos.map((p) => (
                        <MenuItem key={p.conceptoCatalogo} value={p.conceptoCatalogo}>
                          {p.conceptoCatalogo}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{l.unidadNegocio || "—"}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 130 }}>
                  <TextField
                    value={l.importe}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9.]/g, "");
                      if ((raw.match(/\./g) || []).length > 1) return;
                      updateLinea(l.id, "importe", raw);
                    }}
                    size="small" fullWidth
                    inputProps={{ style: { textAlign: "right" } }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton color="error" size="small" onClick={() => removeLinea(l.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap", gap: 1 }}>
          <Button startIcon={<Add />} onClick={addLinea} size="small">Agregar concepto</Button>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2">Suma: <b>${formatCurrency(suma)}</b> / ${formatCurrency(objetivo)}</Typography>
            <Chip size="small" color={sumaOk ? "success" : "warning"}
              label={sumaOk ? "Cuadra" : `Diferencia $${formatCurrency(diferencia)}`} />
            {!sumaOk && lineas.length > 0 && (
              <Button size="small" onClick={distribuirRestante}>Ajustar a la última</Button>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Tooltip title={sumaOk ? "" : "La suma del desglose debe igualar el subtotal"}>
          <span>
            <Button variant="contained" onClick={handleGuardar} disabled={!sumaOk}>
              Guardar desglose
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

export default Ventas;
